import { Section, Text, useViewport } from '@farmersdog/corgi-x';
import { PATH_SIGNUP_ME } from '@farmersdog/constants/paths';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { getLinkToFreshSignup } from '@farmersdog/tosa/src/utils/getLinkToSignupFlow';

import { ExtendedButton } from '../ExtendedButton';
import { CtaTrackerV2, HomepageRedesignSections } from '../CtaTrackerV2';

import { useTrackedSectionRef } from 'src/hooks/useTrackedSections';
import { useLottieAnimation } from 'src/hooks/useLottieAnimation';

import quizDesktopLottie from './assets/quiz-bubble-desktop-lottie.json';
import quizMobileLottie from './assets/quiz-bubble-mobile-lottie.json';

import styles from './PersonalizedPlan.module.css';

const MOBILE_COPY = (
  <Text
    as="p"
    color="kale-3"
    topSpacing="sm"
    bottomSpacing="lg"
    variant="heading-16"
  >
    No two dogs are the same. <br />
    That&apos;s why we tailor every plan based on <br />
    your dog&apos;s breed, age, and size.
  </Text>
);

const DESKTOP_COPY = (
  <Text
    as="p"
    color="kale-3"
    topSpacing="md"
    bottomSpacing="lg"
    variant="heading-22"
  >
    No two dogs are the same. <br />
    That&apos;s why every plan is tailored <br />
    to your dog&apos;s breed, age, and size.
  </Text>
);

export const PersonalizedPlan = () => {
  const linkToSignup = getLinkToFreshSignup(PATH_SIGNUP_ME, {
    skipGetStarted: true,
  });

  const viewport = useViewport();

  const {
    containerRef: desktopLottieContainerRef,
    lottieRefCallback: desktopLottieRefCallback,
    hasScrolled: shouldDesktopLottieMount,
  } = useLottieAnimation();

  const {
    containerRef: mobileLottieContainerRef,
    lottieRefCallback: mobileLottieRefCallback,
    hasScrolled: shouldMobileLottieMount,
  } = useLottieAnimation();

  const sectionRef = useTrackedSectionRef(
    HomepageRedesignSections.PersonalizedPlan
  );

  const isMobile = !viewport?.lg;

  return (
    <Section
      className={styles.section}
      aria-labelledby="personalized-plan-section"
    >
      <div ref={sectionRef} className={styles.container}>
        <div>
          <Text
            className={styles.heading}
            as="h2"
            bold
            variant="heading-28"
            color="kale-3"
          >
            Every plan is <br /> personalized
          </Text>

          {isMobile ? MOBILE_COPY : DESKTOP_COPY}

          <div ref={mobileLottieContainerRef}>
            {isMobile && shouldMobileLottieMount && (
              <DotLottieReact
                loop={false}
                autoplay={false}
                data={quizMobileLottie}
                className={styles.mobileLottie}
                dotLottieRefCallback={mobileLottieRefCallback}
              />
            )}
          </div>
          <CtaTrackerV2
            type="select"
            moduleLevel={1}
            moduleName={HomepageRedesignSections.PersonalizedPlan}
          >
            <ExtendedButton
              type="link"
              to={linkToSignup}
              className={styles.cta}
              variant="bordered-kale"
            >
              Start The Quiz
            </ExtendedButton>
          </CtaTrackerV2>
        </div>
        <div ref={desktopLottieContainerRef}>
          {!isMobile && shouldDesktopLottieMount && (
            <DotLottieReact
              loop={false}
              autoplay={false}
              data={quizDesktopLottie}
              className={styles.desktopLottie}
              dotLottieRefCallback={desktopLottieRefCallback}
            />
          )}
        </div>
      </div>
    </Section>
  );
};
